import { useState, useRef } from 'react';
import AdminLayout from '../../../components/feature/AdminLayout';

export default function AdminSettings() {
  const [activeTab, setActiveTab] = useState('general');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [settings, setSettings] = useState({
    institutionName: '한국디지털교육원',
    institutionDescription: '디지털 시대를 선도하는 전문 교육기관으로, 체계적인 커리큘럼과 실무 중심의 교육을 통해 미래 인재를 양성합니다.',
    websiteUrl: 'https://kdei.co.kr',
    contactEmail: 'admin@kdei.co.kr',
    supportPhone: '02-1234-5678',
    maxFileSize: '10',
    allowedFormats: ['PDF', 'JPG', 'PNG'],
    autoApproval: false,
    emailNotifications: true,
    smsNotifications: false,
    backupFrequency: 'daily',
    retentionPeriod: '7',
    maintenanceMode: false,
    certificateValidation: true,
    publicVerification: true,
    apiRateLimit: '1000',
    requireTwoFactor: false,
    twoFactorEmail: '',
    logoUrl: ''
  });

  const [allowedIPs, setAllowedIPs] = useState([
    { id: 1, ip: '203.0.113.0/24', description: '본사 네트워크', addedDate: '2024-01-01' },
    { id: 2, ip: '198.51.100.10', description: '관리자 사무실', addedDate: '2024-01-05' }
  ]);

  const [newAllowedIP, setNewAllowedIP] = useState({ ip: '', description: '' });

  const [apiSettings, setApiSettings] = useState({
    apiKey: 'sk_live_51234567890abcdefghijklmnopqrstuvwxyz',
    apiEndpoint: 'https://api.kdei.co.kr/v1',
    dailyRequests: 2847,
    dailyLimit: 10000,
    monthlyRequests: 45231,
    monthlyLimit: 100000,
    corsOrigins: ['https://example.com', 'https://partner-site.com'],
    webhookUrl: 'https://your-site.com/webhook/certificates',
    rateLimitPerHour: '1000'
  });

  const [newCorsOrigin, setNewCorsOrigin] = useState('');

  const [currentSubscription] = useState({
    plan: 'pro',
    status: 'active',
    startDate: '2024-01-01',
    nextBillingDate: '2024-02-01',
    amount: 99000,
    templateLimit: 10,
    templatesUsed: 6,
    certificatesIssued: 3247,
    certificateLimit: 10000,
    bulkDownloadUsed: 23,
    bulkDownloadLimit: 100,
    excelColumnLimit: 40
  });

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');

  const subscriptionPlans = [
    {
      id: 'free',
      name: 'Free',
      monthlyPrice: 0,
      yearlyPrice: 0,
      yearlyDiscount: 0,
      features: [
        '템플릿 2개',
        '월 100건 증명서 발급',
        '월 1회 일괄 다운로드',
        '엑셀 업로드 컬럼 10개',
        '유료 수료증 발급 기능',
        '이메일 지원'
      ],
      limits: {
        templates: 2,
        certificates: 100,
        bulkDownload: 1,
        excelColumns: 10,
        paidCertificates: true,
        apiAccess: false,
        support: '이메일'
      }
    },
    {
      id: 'basic',
      name: 'Basic',
      monthlyPrice: 29000,
      yearlyPrice: 295800, // 15% 할인
      yearlyDiscount: 15,
      features: [
        '템플릿 5개',
        '월 1,000건 증명서 발급',
        '월 10회 일괄 다운로드',
        '엑셀 업로드 컬럼 30개',
        '유료 수료증 발급 기능',
        '이메일 지원'
      ],
      limits: {
        templates: 5,
        certificates: 1000,
        bulkDownload: 10,
        excelColumns: 30,
        paidCertificates: true,
        apiAccess: false,
        support: '이메일'
      }
    },
    {
      id: 'pro',
      name: 'Pro',
      monthlyPrice: 99000,
      yearlyPrice: 950400, // 20% 할인
      yearlyDiscount: 20,
      popular: true,
      features: [
        '템플릿 10개',
        '월 10,000건 증명서 발급',
        '월 100회 일괄 다운로드',
        '엑셀 업로드 컬럼 40개',
        '유료 수료증 발급 기능',
        'API 지원',
        '이메일 지원'
      ],
      limits: {
        templates: 10,
        certificates: 10000,
        bulkDownload: 100,
        excelColumns: 40,
        paidCertificates: true,
        apiAccess: true,
        support: '이메일'
      }
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      monthlyPrice: null,
      yearlyPrice: null,
      yearlyDiscount: 0,
      features: [
        '무제한 템플릿',
        '무제한 증명서 발급',
        '무제한 일괄 다운로드',
        '무제한 엑셀 업로드 컬럼',
        '유료 수료증 발급 기능',
        'API 지원',
        '이메일 지원 (SLA 옵션)'
      ],
      limits: {
        templates: -1,
        certificates: -1,
        bulkDownload: -1,
        excelColumns: -1,
        paidCertificates: true,
        apiAccess: true,
        support: '이메일 (SLA 옵션)'
      }
    }
  ];

  const paymentHistory = [
    {
      id: 'INV-2024-001',
      date: '2024-01-01',
      plan: 'Pro',
      amount: 99000,
      status: 'paid',
      method: '신용카드'
    },
    {
      id: 'INV-2023-012',
      date: '2023-12-01',
      plan: 'Pro',
      amount: 99000,
      status: 'paid',
      method: '신용카드'
    },
    {
      id: 'INV-2023-011',
      date: '2023-11-01',
      plan: 'Pro',
      amount: 99000,
      status: 'paid',
      method: '신용카드'
    },
    {
      id: 'INV-2023-010',
      date: '2023-10-01',
      plan: 'Basic',
      amount: 29000,
      status: 'paid',
      method: '신용카드'
    }
  ];

  const handleSettingChange = (key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleApiSettingChange = (key: string, value: any) => {
    setApiSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.includes('png')) {
      alert('PNG 파일만 업로드 가능합니다.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setLogoPreview(result);
      handleSettingChange('logoUrl', result);
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setLogoPreview('');
    handleSettingChange('logoUrl', '');
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  const handleSave = () => {
    alert('설정이 저장되었습니다.');
  };

  const handleReset = () => {
    if (confirm('설정을 초기화하시겠습니까?')) {
      alert('설정이 초기화되었습니다.');
    }
  };

  const handleUpgrade = (planId: string) => {
    if (planId === 'enterprise') {
      // 엔터프라이즈는 이메일 문의
      window.location.href = 'mailto:admin@kdei.co.kr?subject=엔터프라이즈 플랜 문의&body=엔터프라이즈 플랜에 대해 문의드립니다.';
      return;
    }
    setSelectedPlan(planId);
    setShowUpgradeModal(true);
  };

  const handleConfirmUpgrade = () => {
    alert('구독 플랜이 변경되었습니다.');
    setShowUpgradeModal(false);
  };

  const handleCancelSubscription = () => {
    if (confirm('정말 구독을 취소하시겠습니까? 다음 결제일까지 서비스를 이용할 수 있습니다.')) {
      alert('구독이 취소되었습니다.');
    }
  };

  const handleAddAllowedIP = () => {
    if (!newAllowedIP.ip || !newAllowedIP.description) {
      alert('IP 주소와 설명을 모두 입력해주세요.');
      return;
    }
    
    const newIP = {
      id: allowedIPs.length + 1,
      ip: newAllowedIP.ip,
      description: newAllowedIP.description,
      addedDate: new Date().toISOString().split('T')[0]
    };
    
    setAllowedIPs([...allowedIPs, newIP]);
    setNewAllowedIP({ ip: '', description: '' });
    alert('IP가 허용 목록에 추가되었습니다.');
  };

  const handleRemoveAllowedIP = (id: number) => {
    if (confirm('이 IP를 허용 목록에서 제거하시겠습니까?')) {
      setAllowedIPs(allowedIPs.filter(ip => ip.id !== id));
    }
  };

  const handleRegenerateApiKey = () => {
    if (confirm('API 키를 재생성하시겠습니까? 기존 키는 더 이상 사용할 수 없습니다.')) {
      const newKey = 'sk_live_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      handleApiSettingChange('apiKey', newKey);
      alert('새로운 API 키가 생성되었습니다.');
    }
  };

  const handleCopyApiKey = () => {
    navigator.clipboard.writeText(apiSettings.apiKey);
    alert('API 키가 클립보드에 복사되었습니다.');
  };

  const handleAddCorsOrigin = () => {
    if (!newCorsOrigin) {
      alert('도메인을 입력해주세요.');
      return;
    }
    
    if (!newCorsOrigin.startsWith('http://') && !newCorsOrigin.startsWith('https://')) {
      alert('올바른 URL 형식을 입력해주세요. (http:// 또는 https://로 시작)');
      return;
    }
    
    if (apiSettings.corsOrigins.includes(newCorsOrigin)) {
      alert('이미 추가된 도메인입니다.');
      return;
    }
    
    handleApiSettingChange('corsOrigins', [...apiSettings.corsOrigins, newCorsOrigin]);
    setNewCorsOrigin('');
    alert('CORS 허용 도메인이 추가되었습니다.');
  };

  const handleRemoveCorsOrigin = (origin: string) => {
    if (confirm('이 도메인을 CORS 허용 목록에서 제거하시겠습니까?')) {
      handleApiSettingChange('corsOrigins', apiSettings.corsOrigins.filter(o => o !== origin));
    }
  };

  const tabs = [
    { id: 'subscription', name: '구독 관리', icon: 'ri-vip-crown-line' },
    { id: 'general', name: '일반 설정', icon: 'ri-settings-3-line' },
    { id: 'security', name: '보안 및 API', icon: 'ri-shield-line' }
  ];

  const getPlanName = (planId: string) => {
    const plan = subscriptionPlans.find(p => p.id === planId);
    return plan?.name || planId;
  };

  const getPlanPrice = (plan: typeof subscriptionPlans[0]) => {
    if (plan.monthlyPrice === null) return null;
    const price = billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
    return price;
  };

  const getPlanPriceDisplay = (plan: typeof subscriptionPlans[0]) => {
    const price = getPlanPrice(plan);
    if (price === null) return '별도 문의';
    return `₩${price.toLocaleString()}`;
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">시스템 설정</h1>
          <p className="text-gray-600">증명서 발급 시스템의 전반적인 설정을 관리합니다.</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* 탭 메뉴 */}
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6 overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <i className={`${tab.icon} mr-2`}></i>
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* 구독 관리 */}
            {activeTab === 'subscription' && (
              <div className="space-y-6">
                {/* 2026년 프로모션 안내 */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6 border-2 border-purple-200">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <i className="ri-gift-line text-4xl text-purple-600"></i>
                    </div>
                    <div className="ml-4 flex-1">
                      <h3 className="text-xl font-bold text-purple-900 mb-2">
                        🎁 2026년 신규 기관 대상 프로모션
                      </h3>
                      <p className="text-purple-800 mb-3">
                        <strong>2026년 1월 1일 ~ 2026년 3월 1일</strong> 사이에 가입한 기관은 <strong>Basic 플랜을 1년간 무료</strong>로 제공합니다!
                      </p>
                      <ul className="space-y-2 text-sm text-purple-700">
                        <li className="flex items-start">
                          <i className="ri-check-line text-purple-600 mr-2 mt-0.5"></i>
                          <span>이벤트 기간 중 가입 시 자동 적용되며, 1년 후에는 선택한 플랜에 따라 정상 요금 적용</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-check-line text-purple-600 mr-2 mt-0.5"></i>
                          <span>모든 신규 기관에 적용</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-check-line text-purple-600 mr-2 mt-0.5"></i>
                          <span>결제 정보 등록 없이도 Basic 기능 이용 가능</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-check-line text-purple-600 mr-2 mt-0.5"></i>
                          <span>1년 무료 기간 동안 Pro 및 Enterprise로 업그레이드 가능</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-close-line text-red-600 mr-2 mt-0.5"></i>
                          <span>이벤트 종료 후 신청 시 무료 제공되지 않음</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* 현재 구독 정보 */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center mb-2">
                        <i className="ri-vip-crown-fill text-2xl text-yellow-500 mr-2"></i>
                        <h3 className="text-xl font-bold text-gray-900">
                          {getPlanName(currentSubscription.plan)} 플랜
                        </h3>
                      </div>
                      <p className="text-sm text-gray-600">
                        {currentSubscription.status === 'active' ? '활성 구독' : '구독 만료'}
                      </p>
                    </div>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 whitespace-nowrap">
                      <i className="ri-checkbox-circle-fill mr-1"></i>
                      활성
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="bg-white rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-1">월 결제 금액</p>
                      <p className="text-2xl font-bold text-gray-900">₩{currentSubscription.amount.toLocaleString()}</p>
                    </div>
                    <div className="bg-white rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-1">다음 결제일</p>
                      <p className="text-lg font-semibold text-gray-900">{currentSubscription.nextBillingDate}</p>
                    </div>
                    <div className="bg-white rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-1">구독 시작일</p>
                      <p className="text-lg font-semibold text-gray-900">{currentSubscription.startDate}</p>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">사용 현황</h4>
                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600">템플릿 사용</span>
                          <span className="font-medium text-gray-900">
                            {currentSubscription.templatesUsed} / {currentSubscription.templateLimit}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ width: `${(currentSubscription.templatesUsed / currentSubscription.templateLimit) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600">이번 달 증명서 발급</span>
                          <span className="font-medium text-gray-900">
                            {currentSubscription.certificatesIssued.toLocaleString()} / {currentSubscription.certificateLimit.toLocaleString()}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-600 h-2 rounded-full" 
                            style={{ width: `${(currentSubscription.certificatesIssued / currentSubscription.certificateLimit) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600">이번 달 일괄 다운로드</span>
                          <span className="font-medium text-gray-900">
                            {currentSubscription.bulkDownloadUsed} / {currentSubscription.bulkDownloadLimit}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-purple-600 h-2 rounded-full" 
                            style={{ width: `${(currentSubscription.bulkDownloadUsed / currentSubscription.bulkDownloadLimit) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 mt-4">
                    <button 
                      onClick={() => handleUpgrade('enterprise')}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-arrow-up-line mr-2"></i>
                      플랜 업그레이드
                    </button>
                    <button 
                      onClick={handleCancelSubscription}
                      className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-close-circle-line mr-2"></i>
                      구독 취소
                    </button>
                  </div>
                </div>

                {/* 결제 주기 선택 */}
                <div className="flex justify-center">
                  <div className="inline-flex items-center bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setBillingCycle('monthly')}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                        billingCycle === 'monthly'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      월간 결제
                    </button>
                    <button
                      onClick={() => setBillingCycle('yearly')}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                        billingCycle === 'yearly'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      연간 결제
                      <span className="ml-2 text-xs bg-green-100 text-green-600 px-2 py-1 rounded-full whitespace-nowrap">
                        최대 20% 할인
                      </span>
                    </button>
                  </div>
                </div>

                {/* 플랜 비교 */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">플랜 비교</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {subscriptionPlans.map((plan) => (
                      <div 
                        key={plan.id}
                        className={`rounded-lg border-2 p-6 ${
                          plan.popular 
                            ? 'border-blue-500 shadow-lg relative' 
                            : 'border-gray-200'
                        } ${
                          currentSubscription.plan === plan.id
                            ? 'bg-blue-50'
                            : 'bg-white'
                        }`}
                      >
                        {plan.popular && (
                          <div className="absolute top-0 right-0 bg-blue-500 text-white px-3 py-1 text-xs font-medium rounded-bl-lg rounded-tr-lg whitespace-nowrap">
                            인기
                          </div>
                        )}
                        
                        <div className="text-center mb-6">
                          <h4 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h4>
                          <div className="flex items-baseline justify-center min-h-[60px]">
                            {plan.monthlyPrice === null ? (
                              <span className="text-2xl font-bold text-gray-900">별도 문의</span>
                            ) : plan.monthlyPrice === 0 ? (
                              <span className="text-3xl font-bold text-gray-900">무료</span>
                            ) : (
                              <div className="flex flex-col items-center">
                                <div className="flex items-baseline">
                                  <span className="text-3xl font-bold text-gray-900">
                                    {getPlanPriceDisplay(plan)}
                                  </span>
                                  <span className="text-gray-600 ml-2">/ {billingCycle === 'monthly' ? '월' : '년'}</span>
                                </div>
                                {billingCycle === 'yearly' && plan.yearlyDiscount > 0 && (
                                  <span className="text-sm text-green-600 mt-1 whitespace-nowrap">
                                    연간 결제 시 {plan.yearlyDiscount}% 할인
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <ul className="space-y-3 mb-6">
                          {plan.features.map((feature, index) => (
                            <li key={index} className="flex items-start">
                              <i className="ri-check-line text-green-600 mr-2 mt-0.5 flex-shrink-0"></i>
                              <span className="text-sm text-gray-700">{feature}</span>
                            </li>
                          ))}
                        </ul>

                        {currentSubscription.plan === plan.id ? (
                          <button 
                            disabled
                            className="w-full py-2 bg-gray-300 text-gray-600 rounded-lg cursor-not-allowed whitespace-nowrap"
                          >
                            현재 플랜
                          </button>
                        ) : plan.id === 'enterprise' ? (
                          <button 
                            onClick={() => handleUpgrade(plan.id)}
                            className="w-full py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors whitespace-nowrap cursor-pointer"
                          >
                            <i className="ri-mail-line mr-2"></i>
                            이메일 문의
                          </button>
                        ) : (
                          <button 
                            onClick={() => handleUpgrade(plan.id)}
                            className={`w-full py-2 rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                              plan.popular
                                ? 'bg-blue-600 text-white hover:bg-blue-700'
                                : 'border border-blue-600 text-blue-600 hover:bg-blue-50'
                            }`}
                          >
                            {plan.id === 'free' || plan.id === 'basic' ? '다운그레이드' : '업그레이드'}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* 상세 기능 비교표 */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">상세 기능 비교</h3>
                  <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">기능</th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Free</th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Basic</th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Pro</th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Enterprise</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        <tr>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">템플릿 수</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-center">2개</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-center">5개</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-center">10개</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-center">무제한</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">월 발급 수량</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-center">100건</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-center">1,000건</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-center">10,000건</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-center">무제한</td>
                        </tr>
                        <tr>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">일괄 다운로드</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-center">월 1회</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-center">월 10회</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-center">월 100회</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-center">무제한</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">API 지원</td>
                          <td className="px-6 py-4 text-center">
                            <i className="ri-close-line text-red-600 text-xl"></i>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <i className="ri-close-line text-red-600 text-xl"></i>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <i className="ri-check-line text-green-600 text-xl"></i>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <i className="ri-check-line text-green-600 text-xl"></i>
                          </td>
                        </tr>
                        <tr>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">유료 수료증 발급 기능</td>
                          <td className="px-6 py-4 text-center">
                            <i className="ri-check-line text-green-600 text-xl"></i>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <i className="ri-check-line text-green-600 text-xl"></i>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <i className="ri-check-line text-green-600 text-xl"></i>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <i className="ri-check-line text-green-600 text-xl"></i>
                          </td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">엑셀 업로드 컬럼수 제한</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-center">10개</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-center">30개</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-center">40개</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-center">무제한</td>
                        </tr>
                        <tr>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">지원(Support)</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-center">이메일</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-center">이메일</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-center">이메일</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-center">이메일 (SLA 옵션)</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* 결제 내역 */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">결제 내역</h3>
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">청구서 번호</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">날짜</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">플랜</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">금액</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">결제 방법</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">상태</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">액션</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {paymentHistory.map((payment) => (
                          <tr key={payment.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 text-sm font-mono text-gray-900">{payment.id}</td>
                            <td className="px-6 py-4 text-sm text-gray-900">{payment.date}</td>
                            <td className="px-6 py-4 text-sm text-gray-900">{payment.plan}</td>
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">
                              ₩{payment.amount.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900">{payment.method}</td>
                            <td className="px-6 py-4">
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 whitespace-nowrap">
                                결제완료
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <button className="text-blue-600 hover:text-blue-900 text-sm whitespace-nowrap cursor-pointer">
                                <i className="ri-download-line mr-1"></i>
                                영수증
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* 결제 수단 관리 */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">결제 수단</h3>
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <div className="w-12 h-8 bg-gradient-to-r from-blue-600 to-blue-400 rounded flex items-center justify-center mr-4">
                          <i className="ri-bank-card-line text-white"></i>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">신용카드</p>
                          <p className="text-sm text-gray-600">**** **** **** 1234</p>
                        </div>
                      </div>
                      <button className="text-blue-600 hover:text-blue-900 text-sm whitespace-nowrap cursor-pointer">
                        변경
                      </button>
                    </div>
                    <div className="text-sm text-gray-600">
                      <p>만료일: 12/25</p>
                      <p className="mt-1">다음 결제일: {currentSubscription.nextBillingDate}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 일반 설정 */}
            {activeTab === 'general' && (
              <div className="space-y-6">
                {/* 로고 업로드 섹션 */}
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">기관 로고</h3>
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      {logoPreview || settings.logoUrl ? (
                        <div className="relative">
                          <img 
                            src={logoPreview || settings.logoUrl} 
                            alt="기관 로고" 
                            className="w-32 h-32 object-contain border-2 border-gray-300 rounded-lg bg-white p-2"
                          />
                          <button
                            onClick={removeLogo}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors cursor-pointer"
                          >
                            <i className="ri-close-line text-sm"></i>
                          </button>
                        </div>
                      ) : (
                        <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-white">
                          <i className="ri-image-line text-3xl text-gray-400"></i>
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-600 mb-3">
                        PNG 형식의 로고 파일을 업로드하세요. 권장 크기: 512x512px
                      </p>
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept=".png,image/png"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                      <button
                        onClick={() => logoInputRef.current?.click()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-upload-line mr-2"></i>
                        PNG 파일 업로드
                      </button>
                    </div>
                  </div>
                </div>

                {/* 기관 정보 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      기관명 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={settings.institutionName}
                      onChange={(e) => handleSettingChange('institutionName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="기관명을 입력하세요"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      웹사이트 URL
                    </label>
                    <input
                      type="url"
                      value={settings.websiteUrl}
                      onChange={(e) => handleSettingChange('websiteUrl', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      연락처 전화번호 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={settings.supportPhone}
                      onChange={(e) => handleSettingChange('supportPhone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="02-1234-5678"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      이메일 주소 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={settings.contactEmail}
                      onChange={(e) => handleSettingChange('contactEmail', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="contact@example.com"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      기관 소개 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={settings.institutionDescription}
                      onChange={(e) => handleSettingChange('institutionDescription', e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      maxLength={500}
                      placeholder="기관에 대한 간단한 소개를 입력하세요"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {settings.institutionDescription.length} / 500자
                    </p>
                  </div>
                </div>

                {/* 미리보기 */}
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-4">미리보기</h4>
                  <div className="bg-white p-6 rounded-lg border border-gray-200">
                    <div className="flex items-start gap-4 mb-4">
                      {(logoPreview || settings.logoUrl) && (
                        <img 
                          src={logoPreview || settings.logoUrl} 
                          alt="로고" 
                          className="w-16 h-16 object-contain"
                        />
                      )}
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          {settings.institutionName || '기관명'}
                        </h3>
                        <p className="text-sm text-gray-600 mb-3">
                          {settings.institutionDescription || '기관 소개'}
                        </p>
                        <div className="space-y-1 text-sm text-gray-700">
                          {settings.websiteUrl && (
                            <p className="flex items-center">
                              <i className="ri-global-line mr-2 text-gray-400"></i>
                              {settings.websiteUrl}
                            </p>
                          )}
                          {settings.supportPhone && (
                            <p className="flex items-center">
                              <i className="ri-phone-line mr-2 text-gray-400"></i>
                              {settings.supportPhone}
                            </p>
                          )}
                          {settings.contactEmail && (
                            <p className="flex items-center">
                              <i className="ri-mail-line mr-2 text-gray-400"></i>
                              {settings.contactEmail}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 보안 및 API 설정 */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                {/* 2단계 인증 설정 */}
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">2단계 인증 (이메일)</h3>
                  <div className="space-y-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.requireTwoFactor}
                        onChange={(e) => handleSettingChange('requireTwoFactor', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                      <span className="ml-2 text-sm text-gray-700">2단계 인증 활성화</span>
                    </label>
                    
                    {settings.requireTwoFactor && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          인증 코드 수신 이메일 <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          value={settings.twoFactorEmail}
                          onChange={(e) => handleSettingChange('twoFactorEmail', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="인증 코드를 받을 이메일 주소"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          로그인 시 이 이메일로 인증 코드가 전송됩니다.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* IP 허용 목록 (화이트리스트) */}
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">IP 허용 목록 (화이트리스트)</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    관리자 페이지에 접근할 수 있는 IP 주소를 관리합니다. 등록된 IP에서만 접근이 가능합니다.
                  </p>
                  
                  {/* IP 추가 폼 */}
                  <div className="bg-white rounded-lg p-4 mb-4 border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-3">새 IP 허용</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={newAllowedIP.ip}
                        onChange={(e) => setNewAllowedIP({ ...newAllowedIP, ip: e.target.value })}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="IP 주소 또는 CIDR (예: 192.168.1.0/24)"
                      />
                      <input
                        type="text"
                        value={newAllowedIP.description}
                        onChange={(e) => setNewAllowedIP({ ...newAllowedIP, description: e.target.value })}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="설명"
                      />
                    </div>
                    <button
                      onClick={handleAddAllowedIP}
                      className="mt-3 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-check-line mr-2"></i>
                      IP 허용 추가
                    </button>
                  </div>

                  {/* 허용된 IP 목록 */}
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP 주소/범위</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">설명</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">추가일</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">액션</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {allowedIPs.map((ip) => (
                          <tr key={ip.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-mono text-gray-900">{ip.ip}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{ip.description}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{ip.addedDate}</td>
                            <td className="px-4 py-3">
                              <button
                                onClick={() => handleRemoveAllowedIP(ip.id)}
                                className="text-red-600 hover:text-red-900 text-sm whitespace-nowrap cursor-pointer"
                              >
                                <i className="ri-delete-bin-line mr-1"></i>
                                제거
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* 세션 정보 */}
                <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                  <div className="flex items-start">
                    <i className="ri-information-line text-2xl text-blue-600 mr-3 mt-1"></i>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">세션 설정</h3>
                      <p className="text-sm text-gray-700">
                        세션 타임아웃: <strong>1시간</strong> (고정)
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        1시간 동안 활동이 없으면 자동으로 로그아웃됩니다.
                      </p>
                    </div>
                  </div>
                </div>

                {/* API 키 관리 */}
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">API 키 관리</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        API 키
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={apiSettings.apiKey}
                          readOnly
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-100 font-mono text-sm"
                        />
                        <button
                          onClick={handleCopyApiKey}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-file-copy-line mr-2"></i>
                          복사
                        </button>
                        <button
                          onClick={handleRegenerateApiKey}
                          className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-refresh-line mr-2"></i>
                          재생성
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        API 키는 안전하게 보관하세요. 외부에 노출되지 않도록 주의하세요.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        API 엔드포인트
                      </label>
                      <input
                        type="text"
                        value={apiSettings.apiEndpoint}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 font-mono text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* API 사용량 */}
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">API 사용량</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <h4 className="font-medium text-gray-900 mb-3">일일 사용량</h4>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-600">요청 수</span>
                        <span className="font-medium text-gray-900">
                          {apiSettings.dailyRequests.toLocaleString()} / {apiSettings.dailyLimit.toLocaleString()}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${(apiSettings.dailyRequests / apiSettings.dailyLimit) * 100}%` }}
                        ></div>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <h4 className="font-medium text-gray-900 mb-3">월간 사용량</h4>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-600">요청 수</span>
                        <span className="font-medium text-gray-900">
                          {apiSettings.monthlyRequests.toLocaleString()} / {apiSettings.monthlyLimit.toLocaleString()}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-600 h-2 rounded-full" 
                          style={{ width: `${(apiSettings.monthlyRequests / apiSettings.monthlyLimit) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* API 요청 제한 */}
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">API 요청 제한</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      시간당 요청 제한
                    </label>
                    <input
                      type="number"
                      value={apiSettings.rateLimitPerHour}
                      onChange={(e) => handleApiSettingChange('rateLimitPerHour', e.target.value)}
                      className="w-full md:w-1/2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      동일 IP에서 시간당 허용되는 최대 요청 수
                    </p>
                  </div>
                </div>

                {/* CORS 설정 */}
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">CORS 허용 도메인</h3>
                  
                  {/* 도메인 추가 */}
                  <div className="bg-white rounded-lg p-4 mb-4 border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-3">새 도메인 추가</h4>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newCorsOrigin}
                        onChange={(e) => setNewCorsOrigin(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="https://example.com"
                      />
                      <button
                        onClick={handleAddCorsOrigin}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-add-line mr-2"></i>
                        추가
                      </button>
                    </div>
                  </div>

                  {/* 허용된 도메인 목록 */}
                  <div className="space-y-2">
                    {apiSettings.corsOrigins.map((origin, index) => (
                      <div key={index} className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200">
                        <span className="font-mono text-sm text-gray-900">{origin}</span>
                        <button
                          onClick={() => handleRemoveCorsOrigin(origin)}
                          className="text-red-600 hover:text-red-900 text-sm whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-delete-bin-line mr-1"></i>
                          제거
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Webhook 설정 */}
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Webhook 설정</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Webhook URL
                    </label>
                    <input
                      type="url"
                      value={apiSettings.webhookUrl}
                      onChange={(e) => handleApiSettingChange('webhookUrl', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://your-site.com/webhook/certificates"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      증명서 발급 시 이 URL로 알림이 전송됩니다.
                    </p>
                  </div>
                </div>

                {/* API 문서 */}
                <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                  <div className="flex items-start">
                    <i className="ri-book-line text-2xl text-blue-600 mr-3 mt-1"></i>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">API 문서</h3>
                      <p className="text-sm text-gray-700 mb-3">
                        API를 사용하여 증명서 이미지를 조회하고 고객 사이트에 표시할 수 있습니다.
                      </p>
                      <a
                        href="#"
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-file-text-line mr-2"></i>
                        API 문서 보기
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 저장 버튼 */}
            {activeTab !== 'subscription' && (
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  onClick={handleReset}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 whitespace-nowrap cursor-pointer"
                >
                  초기화
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 whitespace-nowrap cursor-pointer"
                >
                  설정 저장
                </button>
              </div>
            )}
          </div>
        </div>

        {/* 플랜 변경 확인 모달 */}
        {showUpgradeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-medium text-gray-900 mb-4">플랜 변경 확인</h3>
              
              <div className="mb-6">
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600">현재 플랜:</span>
                    <span className="font-medium text-gray-900">{getPlanName(currentSubscription.plan)}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600">변경 플랜:</span>
                    <span className="font-medium text-blue-600">{getPlanName(selectedPlan)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">결제 주기:</span>
                    <span className="font-medium text-gray-900">{billingCycle === 'monthly' ? '월간' : '연간'}</span>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    {selectedPlan === 'free' || selectedPlan === 'basic'
                      ? '다운그레이드 시 일부 기능이 제한될 수 있습니다. 다음 결제일부터 적용됩니다.'
                      : '업그레이드 시 즉시 새로운 기능을 사용할 수 있습니다. 차액은 일할 계산되어 청구됩니다.'
                    }
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  취소
                </button>
                <button
                  onClick={handleConfirmUpgrade}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  변경 확인
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
